// A next-game button, or the countdown that stands in its place (CLAUDE.md:
// Every Play and next-game button picks the player's own next puzzle).
//
// The button is the game and the number of the puzzle it opens — "Call Sheet
// #10 →" — chosen for this device (lib/next-puzzle.ts). With every released
// puzzle finished it becomes "Next Call Sheet in 05:12:09", set as the daily's
// countdown is. While the choice is being made, and where nothing is released
// at all, it draws nothing: never a filler.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GameKey } from '../lib/history.ts';
import type { NextState } from '../lib/next-puzzle.ts';

export const GAME_NAMES: Record<GameKey, string> = { 'running-order': 'Running Order', 'call-sheet': 'Call Sheet' };

/**
 * "Next Running Order in 03:08:21", counting to `at`, the moment the next
 * puzzle actually appears (`next_arrivals`). **Nothing at all** where no time
 * is known, and nothing once it reaches zero — never 00:00:00 — until the
 * puzzle is really there, when the button that replaces it is drawn instead.
 */
/**
 * `box` names the classes of the button the countdown stands in for — "next",
 * "btn-block hero-play", "card-play" — and the countdown is then drawn as that
 * button's box (owner, 18 October): the same size and shape, in a dimmer
 * purple, with no arrow, so a pair stays even and it does not look tappable,
 * holding the time alone — "05:09:28".
 * Without `box` it is the plain status line under Running Order's result.
 */
export function NextCountdown({ game, at, box, className = '' }: { game: GameKey; at: number | null | undefined; box?: string; className?: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);
  if (at == null) return null;
  const left = Math.floor((at - now) / 1000);
  if (left <= 0) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(Math.floor(left / 3600))}:${pad(Math.floor(left / 60) % 60)}:${pad(left % 60)}`;
  // **In a box, the time and nothing else** (owner, 18 October), the same in
  // every game and on every page; the words are kept for a screen reader.
  if (box) {
    return (
      <div className={`btn countdown-box ${box}`} role="timer" aria-label={`Next ${GAME_NAMES[game]} in ${time}`}>
        <span className="next-countdown-time">{time}</span>
      </div>
    );
  }
  // One span holding the words and the time: inside a flex box a space left
  // as its own piece of text is dropped, which is how "in12:08:56" happened.
  return (
    <p className={`next-countdown ${className}`.trim()} role="timer">
      <span>{`Next ${GAME_NAMES[game]} in `}<span className="next-countdown-time">{time}</span></span>
    </p>
  );
}

/** The result screens' and notice page's button for one game. */
export function NextButton({ game, next }: { game: GameKey; next: NextState | undefined }) {
  if (!next || next.pick === null) return null;
  if (next.pick === 'done') return <NextCountdown game={game} at={next.arrival} box="next" />;
  return (
    <Link className="btn btn-primary next" to={`/${game}/${next.pick.number}`}>
      <span className="next-name">{GAME_NAMES[game]} #{next.pick.number}</span>
      <span className="next-arrow" aria-hidden="true">→</span>
    </Link>
  );
}
